import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../services/api';
import {
  ArrowLeftIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import AgentProjectsManager from '../components/Agents/AgentProjectsManager';

const AgentDetailPage = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('messages');
  const [activityView, setActivityView] = useState('messages');

  const { data: agentData, isLoading: agentLoading, error: agentError } = useQuery(
    ['agent', id],
    () => api.get(`/agents/${id}`).then((res) => res.data),
    {
      enabled: !!id,
      retry: false,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    }
  );

  const { data: messagesData, isLoading: messagesLoading } = useQuery(
    ['agentMessages', id],
    () => api.get(`/agents/${id}/messages`, { params: { limit: 200 } }).then((res) => res.data),
    {
      enabled: !!id && activeTab === 'messages' && activityView === 'messages',
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchInterval: 15000,
    }
  );

  const { data: chainsData, isLoading: chainsLoading } = useQuery(
    ['agentChains', id],
    () => api.get(`/agents/${id}/chains`, { params: { limit: 200 } }).then((res) => res.data),
    {
      enabled: !!id && activeTab === 'messages' && activityView === 'chains',
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchInterval: 30000,
    }
  );

  if (agentLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const agent = agentData?.agent;
  const stats = agentData?.stats;

  if (agentError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Error loading agent: {agentError.message}</p>
        <Link to="/agents" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to agents
        </Link>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Agent not found (ID: {id})</p>
        <Link to="/agents" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to agents
        </Link>
      </div>
    );
  }

  const displayName = `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || `@${agent.username}`;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/agents" className="inline-flex items-center text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Friendly Agents
        </Link>

        <div className="flex flex-wrap items-start gap-6">
          <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-bold text-blue-600">
              {agent.username?.[0]?.toUpperCase() || agent.first_name?.[0]?.toUpperCase() || '?'}
            </span>
          </div>

          <div className="flex-1 min-w-[240px]">
            <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
            <p className="text-gray-500">@{agent.username}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-green-100 px-3 py-1 text-green-800">friendly</span>
              <span className={`rounded-full px-3 py-1 ${agent.user_id ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>
                {agent.user_id ? 'resolved' : 'unresolved'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <StatBox label="Messages" value={stats?.total_messages || 0} />
            <StatBox label="Chats" value={stats?.unique_chats || 0} />
            <StatBox label="User ID" value={agent.user_id || '-'} compact />
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'messages', label: 'Activity log' },
            { id: 'info', label: 'Info' },
            { id: 'projects', label: 'Projects' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'messages' && (
        <div className="space-y-3">
          <div className="inline-flex min-h-[44px] rounded-lg border border-gray-200 bg-white p-1" role="tablist" aria-label="Agent activity log view">
            {[
              ['messages', `Messages · ${messagesData?.total ?? stats?.total_messages ?? 0}`],
              ['chains', `Chains · ${chainsData?.total ?? stats?.chains_participated ?? 0}`],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={activityView === value}
                onClick={() => setActivityView(value)}
                className={`min-h-[36px] rounded-md px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  activityView === value
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {activityView === 'messages' ? (
            <MessagesTab
              messages={messagesData?.messages || []}
              loading={messagesLoading}
              total={messagesData?.total || 0}
            />
          ) : (
            <ChainsTab
              chains={chainsData?.chains || []}
              loading={chainsLoading}
              total={chainsData?.total || 0}
            />
          )}
        </div>
      )}

      {activeTab === 'info' && <InfoTab agent={agent} stats={stats} />}

      {activeTab === 'projects' && (
        <AgentProjectsManager
          agentId={agent.id}
          agentName={agent.username ? `@${agent.username}` : displayName}
        />
      )}
    </div>
  );
};

const StatBox = ({ label, value, compact = false }) => (
  <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 min-w-[96px]">
    <div className={`${compact ? 'text-sm' : 'text-2xl'} font-bold text-gray-900 truncate`}>{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

const InfoTab = ({ agent, stats }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Telegram identity</h3>
      <div className="space-y-4">
        <InfoRow icon={UserCircleIcon} label="Username" value={agent.username ? `@${agent.username}` : '-'} />
        <InfoRow icon={UserCircleIcon} label="User ID" value={agent.user_id || '-'} />
        <InfoRow icon={UserCircleIcon} label="First name" value={agent.first_name || '-'} />
        <InfoRow icon={UserCircleIcon} label="Last name" value={agent.last_name || '-'} />
        <InfoRow icon={CalendarIcon} label="Created" value={agent.created_at ? new Date(agent.created_at).toLocaleString() : '-'} />
      </div>
    </div>

    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Tracking</h3>
      <div className="space-y-4">
        <InfoRow icon={ChatBubbleLeftRightIcon} label="Observed messages" value={stats?.total_messages || 0} />
        <InfoRow icon={ChatBubbleLeftRightIcon} label="Observed chats" value={stats?.unique_chats || 0} />
        <InfoRow icon={CalendarIcon} label="Last message" value={stats?.last_message_at ? new Date(stats.last_message_at).toLocaleString() : '-'} />
        <InfoRow icon={UserCircleIcon} label="Source" value={agent.source || '-'} />
      </div>
    </div>

    {agent.notes && (
      <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
        <p className="text-gray-700 whitespace-pre-wrap">{agent.notes}</p>
      </div>
    )}
  </div>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="h-5 w-5 text-gray-400 mt-0.5" />
    <div>
      <div className="text-sm text-gray-500">{label}</div>
      <div className="text-gray-900 break-all">{value}</div>
    </div>
  </div>
);

const MessagesTab = ({ messages, loading, total }) => {
  if (loading) {
    return <div className="text-center py-8">Loading messages...</div>;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold">Observed Messages ({total})</h3>
        <p className="text-xs text-gray-500">Every message from this friendly agent that TeleFarm sees.</p>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No observed messages yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {messages.map((message) => (
            <div key={message.id} className="p-4">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span>{message.sent_at ? new Date(message.sent_at).toLocaleString() : '-'}</span>
                <span>·</span>
                <span>{message.chat_title || message.project_name || message.chat_id || '-'}</span>
                <span>·</span>
                <span>{message.content_type || 'message'}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-gray-800">{message.message_text || '(empty/non-text message)'}</p>
              <p className="mt-2 text-xs text-gray-400">
                chat_id: {message.chat_id || '-'} · message_id: {message.message_id || '-'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const getProjectAssignmentLabel = (chain) => {
  if (chain.assignment_status === 'ambiguous') return { label: 'Needs project', className: 'bg-yellow-100 text-yellow-800' };
  if (chain.assignment_status === 'unassigned' || chain.assignment_status === 'failed') {
    return { label: 'No project', className: 'bg-gray-100 text-gray-700' };
  }
  if (chain.project_name) {
    const confirmed = chain.assignment_source === 'human';
    return {
      label: `${chain.project_name}${confirmed ? ' · Confirmed' : ' · Suggested'}`,
      className: confirmed ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800',
    };
  }
  if (chain.suggested_project_name) {
    return { label: `${chain.suggested_project_name} · Needs project`, className: 'bg-yellow-100 text-yellow-800' };
  }
  return { label: 'Project pending', className: 'bg-gray-100 text-gray-700' };
};

const ChainsTab = ({ chains, loading, total }) => {
  const [expandedId, setExpandedId] = useState(null);
  if (loading) return <div className="text-center py-8">Loading chains...</div>;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-6 py-4">
        <h3 className="text-lg font-semibold">Activity Chains ({total})</h3>
        <p className="text-xs text-gray-500">One row per single-message or multi-message activity episode.</p>
      </div>
      {chains.length === 0 ? (
        <div className="py-12 text-center text-gray-500">No activity chains yet</div>
      ) : (
        <div className="divide-y divide-gray-100">
          {chains.map((chain) => {
            const assignment = getProjectAssignmentLabel(chain);
            const expanded = expandedId === chain.id;
            const date = chain.ended_at || chain.last_response_at || chain.last_activity_at;
            const preview = chain.trigger_message_text || 'Activity without stored trigger text';
            return (
              <div key={chain.id}>
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setExpandedId(expanded ? null : chain.id)}
                  className="grid min-h-[56px] w-full grid-cols-1 items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:grid-cols-[150px_auto_minmax(180px,1fr)_auto]"
                >
                  <span className="text-xs text-gray-500">{date ? new Date(date).toLocaleString() : 'Date unavailable'}</span>
                  <span className={`w-fit rounded-full px-2 py-1 text-xs font-medium ${chain.status === 'completed' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'}`}>
                    {chain.status === 'completed' ? 'Completed' : 'Active'}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900">{chain.chat_title || chain.trigger_chat_id || 'Unknown chat'}</span>
                    <span className="block truncate text-xs text-gray-500">{preview}</span>
                  </span>
                  <span className="flex flex-wrap items-center gap-2 md:justify-end">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${assignment.className}`}>{assignment.label}</span>
                    <span className="text-xs text-gray-500">{chain.agent_messages_count || 1} message{Number(chain.agent_messages_count || 1) === 1 ? '' : 's'}</span>
                  </span>
                </button>
                {expanded && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                    <p className="whitespace-pre-wrap break-words">{preview}</p>
                    {chain.assignment_reason_summary && (
                      <p className="mt-2 text-xs text-gray-500">Project match: {chain.assignment_reason_summary}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AgentDetailPage;
